import { useLocation, useNavigate } from "react-router-dom"
import { StepProps } from "../../../../components/Stepper"
import { BuyData, PayInfo, isBuyRouteState } from "../../../../types/steps/buy"
import { useRequest } from "../../../../hooks/useRequest"
import { appStore } from "../../../../store/appStore"
import { userStore } from "../../../../store/userStore"
import { useEffect, useMemo, useRef, useState } from "react"
import { filterSessionsForSessionId } from "../../../../lib/sessionGroups"
import { Place, Session } from "../../../../types/entities/session"
import { StartPopupData } from "../../../../types/entities/startPopup"
import Loader from "../../../../components/Loader"
import TermsPopup from "../../../../components/TermsPopup"
import { Cross } from "../../../../components/svgCollection"
import { SessionString } from "../../../../features/home"
import { Hall } from "../../../../features/buy/Hall"
import { SpiderManSeatIcon } from "../../../../features/buy/SpiderManSeatIcon"
import CinemaConfirmHint from "../../../../features/buy/CinemaConfirmHint"
import { isSpiderManFilm } from "../../../../lib/spiderManFilm"
import { hrefForCurrentCinema } from "../../../../lib/cinemaSlugs"
import { clearBuyDraft, loadBuyDraft, saveBuyDraft } from "../../../../lib/buyDraft"
import { fixMonthDeclension } from "../../../../lib/ruText"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

const META_ITEMS = [
    { key: "hall", label: "Зал" },
    { key: "format", label: "Формат" },
    { key: "date", label: "Дата" },
    { key: "time", label: "Время" },
] as const

function formatSessionDateLabel(raw: string) {
    return new Date(raw.replace(" ", "T")).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
    })
}

function useIsMobile() {
    const [mobile, setMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    )

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)")
        const update = () => setMobile(mq.matches)
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return mobile
}

/** SE / 7 / 8 и похожие: мало высоты + мобильная ширина */
function useIsShortMobile() {
    const query = "(max-height: 740px) and (max-width: 767px)"
    const [short, setShort] = useState(
        () => typeof window !== "undefined" && window.matchMedia(query).matches,
    )

    useEffect(() => {
        const mq = window.matchMedia(query)
        const update = () => setShort(mq.matches)
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return short
}

export default function Seats({ navigate, goBack }: StepProps<BuyData>) {
    const router = useNavigate()
    const { request, requestData } = useRequest()
    const { cinema, city } = appStore()
    const { user } = userStore()

    const location = useLocation()
    const buyState = isBuyRouteState(location.state) ? location.state : null

    useEffect(() => {
        if (!buyState) {
            router(hrefForCurrentCinema("/afisha"), { replace: true })
        }
    }, [buyState, router])

    if (!buyState) return <Loader />

    const { sessionId, movieData, sessions } = buyState
    const state = location.state

    const [selectedSeats, setSelectedSeats] = useState<Place[]>([])
    const [sessionData, setSessionData] = useState<Session | null>(null)
    const [selectedSessionId, setSelectedSessionId] = useState<number>(sessionId)
    const [sessionModal, setSessionModal] = useState<StartPopupData | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [switching, setSwitching] = useState(false)
    const isMobile = useIsMobile()
    const isShortMobile = useIsShortMobile()
    const prevSessionIdRef = useRef<number | null>(null)
    const hadSeatsRef = useRef(false)
    const fetchSeqRef = useRef(0)
    const hasSessionRef = useRef(false)

    const sessionsForDay = useMemo(
        () => filterSessionsForSessionId(sessions, selectedSessionId),
        [sessions, selectedSessionId],
    )

    const restoreSeatsFromSession = (data: Session, prevSelected: Place[], forSessionId: number) => {
        const places = data.hall.levels?.[0]?.places ?? []
        const availableIds = new Set(places.filter(place => place.status).map(place => place.id))

        if (prevSelected.length === 0) {
            const draft = loadBuyDraft()
            if (draft?.sessionId === forSessionId) {
                return places.filter(
                    place => draft.selectedSeatIds.includes(place.id) && availableIds.has(place.id),
                )
            }
        }

        return prevSelected.filter(seat => availableIds.has(seat.id))
    }

    const fetchSession = async (forSessionId: number, silent = false) => {
        const requestId = ++fetchSeqRef.current
        if (!silent && hasSessionRef.current) setSwitching(true)

        try {
            const data: Session = await request(
                "GET",
                `cinema/${cinema.id}/session/${forSessionId}`,
                undefined,
                // фоновое обновление не должно показывать гостю ошибку каждые пять секунд
                silent ? { disableError: true } : undefined,
            )
            if (requestId !== fetchSeqRef.current) return

            hasSessionRef.current = true
            setSessionData(data)
            setSelectedSeats(prev => restoreSeatsFromSession(data, prev, forSessionId))
        } finally {
            if (requestId === fetchSeqRef.current) setSwitching(false)
        }
    }

    useEffect(() => {
        if (!selectedSessionId) return

        if (prevSessionIdRef.current !== null && prevSessionIdRef.current !== selectedSessionId) {
            setSelectedSeats([])
            clearBuyDraft()
        }
        prevSessionIdRef.current = selectedSessionId

        fetchSession(selectedSessionId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSessionId])

    useEffect(() => {
        if (selectedSeats.length === 0) {
            if (hadSeatsRef.current) clearBuyDraft()
            hadSeatsRef.current = false
            return
        }

        hadSeatsRef.current = true
        saveBuyDraft({
            sessionId: selectedSessionId,
            selectedSeatIds: selectedSeats.map(seat => seat.id),
        })
    }, [selectedSeats, selectedSessionId])

    useEffect(() => {
        if (!selectedSessionId) return

        let failures = 0

        const intervalId = window.setInterval(() => {
            // забытая вкладка живёт сутками, а сеанс за это время успевает закончиться
            if (document.hidden) return

            fetchSession(selectedSessionId, true)
                .then(() => {
                    failures = 0
                })
                .catch(() => {
                    failures += 1
                    // сеанс сняли или он прошёл: обновлять больше нечего
                    if (failures >= 3) window.clearInterval(intervalId)
                })
        }, 5000)

        return () => window.clearInterval(intervalId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSessionId])

    if (!sessionData) return <Loader />

    const selectedPreview = sessionsForDay.find(item => item.id === selectedSessionId)
    const date = formatSessionDateLabel(selectedPreview?.datetime_at || sessionData.datetime)
    const time = selectedPreview?.time ?? sessionData.datetime.slice(11, 16)
    const disabled = switching || selectedSeats.length === 0
    const level = sessionData.hall.levels?.[0]

    const payInfo: PayInfo = {
        selectedSeats,
        film_name: sessionData.film_name,
        description: sessionData.description,
        format: sessionData.format,
        totalPrice: selectedSeats.reduce((accumulator, seat) => accumulator + seat.price, 0),
        selectedSession: selectedSessionId,
        privileges: movieData.privileges,
        poster: movieData.poster,
        poster_mobile: movieData.poster_mobile,
        other: {
            cinemaName: sessionData.cinema_name,
            hallNumber: sessionData.hall.name,
            date,
            time,
        },
    }

    const spiderMan = isSpiderManFilm(sessionData.film_name)
    const seatsCount = selectedSeats.length
    const seatsLabel = seatsCount === 1 ? "место" : seatsCount <= 4 ? "места" : "мест"
    const totalLabel =
        seatsCount > 0
            ? `${seatsCount} ${seatsLabel}, ${payInfo.totalPrice.toLocaleString("ru-RU", { currency: "RUB", style: "currency", maximumFractionDigits: 0 })}`
            : "Выберите места"

    const metaValues = {
        hall: sessionData.hall.name,
        format: (selectedPreview?.format ?? sessionData.format).toUpperCase(),
        date,
        time,
    }

    const sessionMessages =
        sessionData.messages?.length > 0 ? (
            <>
                {sessionData.messages.map((message, index) => (
                    <div
                        key={index}
                        className={`flex items-center gap-2 rounded-full bg-white/[0.06] ${
                            isShortMobile ? "gap-1.5 px-2.5 py-1" : "px-3 py-1.5"
                        }`}>
                        <img
                            src={message.icon.svg}
                            alt=""
                            className={`shrink-0 ${isShortMobile ? "h-3.5 w-3.5" : "h-4 w-4"}`}
                        />
                        <span
                            className={`leading-tight text-white/75 ${isShortMobile ? "text-[11px]" : "text-[12px]"}`}
                            style={{ fontFamily: BODY, fontWeight: 600 }}>
                            {fixMonthDeclension(message.text)}
                        </span>
                    </div>
                ))}
            </>
        ) : null

    const openLocationPicker = () => {
        const bg = (location.state as { backgroundLocation?: typeof location })?.backgroundLocation
        router("/location", { state: { backgroundLocation: bg ?? location } })
    }

    const proceedToPay = () => {
        clearBuyDraft()
        navigate("pay", { payInfo })
    }

    const submit = async () => {
        if (!user?.token) {
            if (selectedSeats.length > 0) {
                saveBuyDraft({
                    sessionId: selectedSessionId,
                    selectedSeatIds: selectedSeats.map(seat => seat.id),
                })
            }
            router("/auth", {
                state: {
                    ...(state ?? {}),
                    backgroundLocation: (state as { backgroundLocation?: typeof location })?.backgroundLocation ?? location,
                },
            })
            return
        }

        setSubmitting(true)
        try {
            const res = await requestData<StartPopupData | null>({
                type: "GET",
                path: `cinema/${cinema.id}/session/${selectedSessionId}/modal`,
                disableError: true,
            })
            if (res.status === "success" && res.data) {
                setSessionModal(res.data)
            } else {
                proceedToPay()
            }
        } catch {
            proceedToPay()
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            {sessionModal && (
                <TermsPopup
                    data={sessionModal}
                    bodyAlign="left"
                    onConfirm={() => {
                        setSessionModal(null)
                        proceedToPay()
                    }}
                    onDismiss={() => setSessionModal(null)}
                />
            )}
            <div
                className={`flex h-full min-h-0 flex-1 flex-col px-5 md:gap-3 md:px-8 md:py-4 ${
                    isShortMobile ? "py-2" : "py-3"
                }`}>
                <div className="flex shrink-0 flex-col gap-2 md:gap-2">
                    <div className="flex w-full items-start justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-3">
                            <h2
                                className="text-white leading-tight"
                                style={{
                                    fontFamily: DISPLAY,
                                    fontWeight: 800,
                                    fontSize: isShortMobile ? "clamp(15px, 3vw, 20px)" : "clamp(16px, 2.7vw, 24px)",
                                    letterSpacing: "-0.02em",
                                }}>
                                {sessionData.film_name}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={goBack}
                            className="hidden shrink-0 cursor-pointer rounded-full p-1 text-white/70 transition-colors hover:text-white md:block"
                            aria-label="Закрыть">
                            <Cross />
                        </button>
                    </div>

                    <div
                        className={`flex w-full flex-wrap items-start justify-between overflow-visible sm:gap-x-8 ${
                            isShortMobile ? "gap-x-3 gap-y-1" : "gap-x-4 gap-y-1"
                        }`}>
                        <div
                            className={`flex flex-wrap items-start sm:gap-x-8 ${
                                isShortMobile ? "gap-x-3 gap-y-1" : "gap-x-4 gap-y-1"
                            }`}>
                            <CinemaConfirmHint
                                cinemaId={cinema.id}
                                label="Кинотеатр"
                                cinemaName={sessionData.cinema_name}
                                cityName={city.name}
                                address={cinema.address}
                                onYes={() => {}}
                                onNo={openLocationPicker}
                            />
                            {META_ITEMS.map(item => (
                                <div key={item.key} className="flex flex-col items-start gap-px">
                                    <span
                                        className={`uppercase tracking-[0.06em] text-white/40 ${
                                            isShortMobile ? "text-[9px]" : "text-[10px]"
                                        }`}
                                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        {item.label}
                                    </span>
                                    <span
                                        className={`leading-tight text-white/85 ${
                                            isShortMobile ? "text-[12px]" : "text-[13px]"
                                        }`}
                                        style={{ fontFamily: BODY, fontWeight: 600 }}>
                                        {metaValues[item.key]}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {sessionMessages && (
                            <div className="hidden flex-wrap items-center justify-end gap-x-3 gap-y-2 md:flex">{sessionMessages}</div>
                        )}
                    </div>

                    <div
                        className={`min-w-0 overflow-hidden border-t border-white/10 md:pt-2 ${
                            isShortMobile ? "pt-1" : "pt-2"
                        }`}>
                        <SessionString
                            sessions={sessionsForDay}
                            movieData={movieData}
                            selectedSession={selectedSessionId}
                            setSelectedSession={setSelectedSessionId}
                            center
                            scrollable
                        />
                    </div>
                </div>

                <div
                    className={`relative flex min-h-0 flex-1 flex-col border-t border-white/10 md:min-h-0 md:pt-2 ${
                        isMobile
                            ? `basis-0 overflow-hidden ${isShortMobile ? "pt-1" : "pt-2"}`
                            : "pt-3"
                    }`}
                    aria-busy={switching}>
                    <div
                        className={`flex min-h-0 flex-1 flex-col transition-opacity duration-200 ${
                            switching ? "pointer-events-none opacity-35" : "opacity-100"
                        }`}>
                        <Hall
                            level={level}
                            selectedSeats={selectedSeats}
                            setSelectedSeats={setSelectedSeats}
                            prices={sessionData.prices}
                            fillHeight={isMobile}
                            containerHeight={isMobile ? undefined : 620}
                            initialZoomSteps={isMobile ? 0 : 1}
                            spiderMan={spiderMan}
                        />
                    </div>
                    {switching && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                            <img
                                src={require("../../../../assets/pop-corn.gif")}
                                alt=""
                                className="size-[72px] object-contain opacity-90"
                            />
                        </div>
                    )}
                </div>

                <div
                    className={`mt-auto flex shrink-0 flex-col border-t border-white/10 pb-[max(0.5rem,var(--safe-bottom))] md:flex-row md:items-center md:gap-3 md:pt-2 ${
                        isShortMobile ? "gap-1.5 pt-1.5" : "gap-2 pt-2"
                    }`}>
                    {sessionMessages && (
                        <div
                            className={`flex w-full flex-wrap items-center justify-center md:hidden ${
                                isShortMobile ? "gap-x-2 gap-y-1" : "gap-x-3 gap-y-2"
                            }`}>
                            {sessionMessages}
                        </div>
                    )}
                    <div
                        className={`flex min-w-0 flex-1 flex-wrap items-center justify-center md:justify-start md:gap-x-4 ${
                            isShortMobile ? "gap-x-2 gap-y-1" : "gap-x-3 gap-y-1.5"
                        }`}>
                        {sessionData.prices.map((item, index) => (
                            <div className="flex items-center gap-1.5" key={index}>
                                <div
                                    style={{ backgroundColor: item.color }}
                                    className={`shrink-0 rounded-[4px] ${isShortMobile ? "h-3 w-3" : "h-4 w-4"}`}
                                />
                                <span
                                    className={`text-white/60 ${isShortMobile ? "text-[10px]" : "text-[11px]"}`}
                                    style={{ fontFamily: MONO, fontWeight: 600 }}>
                                    {item.name}
                                </span>
                            </div>
                        ))}
                        <div className="flex items-center gap-1.5">
                            {spiderMan ? (
                                <SpiderManSeatIcon
                                    variant="selected"
                                    className={`shrink-0 ${isShortMobile ? "h-4 w-4" : "h-5 w-5"}`}
                                />
                            ) : (
                                <div
                                    className={`bg-accent shrink-0 rounded-[4px] ${
                                        isShortMobile ? "h-3 w-3" : "h-4 w-4"
                                    }`}
                                />
                            )}
                            <span
                                className={`text-white/60 ${isShortMobile ? "text-[10px]" : "text-[11px]"}`}
                                style={{ fontFamily: MONO, fontWeight: 600 }}>
                                Выбранные места
                            </span>
                        </div>
                        {spiderMan && (
                            <div className="flex items-center gap-1.5">
                                <SpiderManSeatIcon
                                    variant="occupied"
                                    className={`shrink-0 ${isShortMobile ? "h-4 w-4" : "h-5 w-5"}`}
                                />
                                <span
                                    className={`text-white/60 ${isShortMobile ? "text-[10px]" : "text-[11px]"}`}
                                    style={{ fontFamily: MONO, fontWeight: 600 }}>
                                    Занятые места
                                </span>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        disabled={disabled || submitting}
                        onClick={() => submit()}
                        className={`flex h-11 min-h-11 w-full shrink-0 cursor-pointer items-center justify-center rounded-[14px] px-5 text-[14px] font-semibold tabular-nums sm:text-[15px] md:h-12 md:min-h-12 md:w-auto md:min-w-[200px] ${disabled || submitting ? "bg-white/10 text-white/40" : "bg-accent text-white shadow-[0_0_24px_rgba(228,32,107,0.35)]"}`}
                        style={{ fontFamily: BODY }}>
                        {submitting ? "Загрузка..." : totalLabel}
                    </button>
                </div>
            </div>
        </>
    )
}
